import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Download, ArrowUpDown, Pencil } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { ReceiptUpload } from "@/components/admin/ReceiptUpload";
import { exportToExcel } from "@/utils/excelExport";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { HST_RATE } from "@/types/crm";
import { logAudit } from "@/utils/auditLog";
import { useAuth } from "@/hooks/useAuth";

type BusinessExpense = Tables<"business_expenses">;

const BUSINESS_CATEGORIES = [
  { value: "Rent/Lease", hstDefault: true },
  { value: "Utilities", hstDefault: true },
  { value: "Internet/Phone", hstDefault: true },
  { value: "Advertising", hstDefault: true },
  { value: "Office Supplies", hstDefault: true },
  { value: "Software/Subscriptions", hstDefault: true },
  { value: "Professional Fees", hstDefault: true },
  { value: "Insurance (Business)", hstDefault: false },
  { value: "Bank Fees", hstDefault: false },
  { value: "Licensing/Permits", hstDefault: false },
  { value: "Vehicle (Business Use)", hstDefault: true },
  { value: "Other", hstDefault: true },
];

export default function BusinessExpenses() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<BusinessExpense[]>([]);
  const [editingExpense, setEditingExpense] = useState<BusinessExpense | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [datePeriod, setDatePeriod] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [sortField, setSortField] = useState<"date" | "category" | "amount">("date");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
  const [deleteTarget, setDeleteTarget] = useState<BusinessExpense | null>(null);
  const [formData, setFormData] = useState({
    category: "",
    description: "",
    amount: "",
    hst_applicable: true,
    hst_amount: "",
    expense_date: `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}`,
    receipt_url: null as string | null,
    receipt_filename: null as string | null,
  });

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("business_expenses")
      .select("*")
      .is("deleted_at", null)
      .order("expense_date", { ascending: false });

    if (error) toast.error("Failed to load expenses");
    else setExpenses(data || []);
    setIsLoading(false);
  };

  // Date filtering logic — matching FinancialDashboard
  const getDateRange = (): { start: Date; end: Date } | null => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const quarter = Math.floor(month / 3);

    switch (datePeriod) {
      case "month":
        return { start: new Date(year, month, 1), end: new Date(year, month + 1, 0) };
      case "last_month": {
        const lm = month === 0 ? 11 : month - 1;
        const ly = month === 0 ? year - 1 : year;
        return { start: new Date(ly, lm, 1), end: new Date(ly, lm + 1, 0) };
      }
      case "quarter":
        return { start: new Date(year, quarter * 3, 1), end: new Date(year, (quarter + 1) * 3, 0) };
      case "last_quarter": {
        const lq = quarter === 0 ? 3 : quarter - 1;
        const lqy = quarter === 0 ? year - 1 : year;
        return { start: new Date(lqy, lq * 3, 1), end: new Date(lqy, (lq + 1) * 3, 0) };
      }
      case "year":
        return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
      case "last_year":
        return { start: new Date(year - 1, 0, 1), end: new Date(year - 1, 11, 31) };
      case "custom":
        if (customStart && customEnd) {
          return { start: new Date(customStart + "T00:00:00"), end: new Date(customEnd + "T23:59:59") };
        }
        return null;
      default:
        return null;
    }
  };

  const isInDateRange = (dateStr: string) => {
    const range = getDateRange();
    if (!range) return true;
    // Append T00:00:00 to force local-time parsing; bare date strings are parsed as UTC
    const date = new Date(dateStr + "T00:00:00");
    return date >= range.start && date <= range.end;
  };

  const handleAmountChange = (value: string) => {
    setFormData((prev) => {
      const amount = parseFloat(value) || 0;
      const hst = prev.hst_applicable ? parseFloat((amount * HST_RATE).toFixed(2)) : 0;
      return { ...prev, amount: value, hst_amount: hst.toString() };
    });
  };

  const handleHstToggle = (checked: boolean) => {
    setFormData((prev) => {
      const amount = parseFloat(prev.amount) || 0;
      const hst = checked ? parseFloat((amount * HST_RATE).toFixed(2)) : 0;
      return { ...prev, hst_applicable: checked, hst_amount: hst.toString() };
    });
  };

  const handleCategoryChange = (value: string) => {
    const cat = BUSINESS_CATEGORIES.find((c) => c.value === value);
    const hstApplicable = cat?.hstDefault ?? true;
    const amount = parseFloat(formData.amount) || 0;
    const hst = hstApplicable ? parseFloat((amount * HST_RATE).toFixed(2)) : 0;
    setFormData((prev) => ({
      ...prev, category: value, hst_applicable: hstApplicable, hst_amount: hst.toString(),
    }));
  };

  const resetForm = () => {
    setFormData({
      category: "", description: "", amount: "", hst_applicable: true, hst_amount: "",
      expense_date: new Date().toISOString().split("T")[0], receipt_url: null, receipt_filename: null,
    });
    setEditingExpense(null);
    setIsAdding(false);
  };

  const openEdit = (expense: BusinessExpense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      description: expense.description || "",
      amount: expense.amount.toString(),
      hst_applicable: expense.hst_applicable,
      hst_amount: expense.hst_amount.toString(),
      expense_date: expense.expense_date,
      receipt_url: expense.receipt_url,
      receipt_filename: expense.receipt_filename,
    });
    setIsAdding(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category || !formData.amount) return;

    const amountVal = parseFloat(formData.amount);
    const hstAmountVal = parseFloat(formData.hst_amount) || 0;
    if (amountVal < 0 || hstAmountVal < 0) {
      toast.error("Expense amounts cannot be negative");
      return;
    }

    setIsSaving(true);

    const { data: { user: authUser } } = await supabase.auth.getUser();

    if (editingExpense) {
      const { error } = await supabase
        .from("business_expenses")
        .update({
          category: formData.category,
          description: formData.description || null,
          amount: amountVal,
          hst_amount: hstAmountVal,
          hst_applicable: formData.hst_applicable,
          expense_date: formData.expense_date,
          receipt_url: formData.receipt_url,
          receipt_filename: formData.receipt_filename,
        })
        .eq("id", editingExpense.id);

      if (error) {
        toast.error("Failed to update expense");
      } else {
        toast.success("Expense updated");
        await logAudit({
          entityType: 'expense',
          entityId: editingExpense.id,
          action: 'updated',
          metadata: {
            category: formData.category,
            amount: amountVal,
            hst_amount: hstAmountVal,
            expense_date: formData.expense_date,
            expense_type: 'business',
            description: formData.description || null,
          },
          performedBy: authUser?.id,
        });
        resetForm();
        loadExpenses();
      }
    } else {
      const { error } = await supabase.from("business_expenses").insert({
        category: formData.category,
        description: formData.description || null,
        amount: amountVal,
        hst_amount: hstAmountVal,
        hst_applicable: formData.hst_applicable,
        expense_date: formData.expense_date,
        receipt_url: formData.receipt_url,
        receipt_filename: formData.receipt_filename,
        created_by: authUser?.id || null,
      });

      if (error) {
        toast.error("Failed to add expense");
      } else {
        toast.success("Expense added");
        await logAudit({
          entityType: 'expense',
          action: 'created',
          metadata: {
            category: formData.category,
            amount: amountVal,
            hst_amount: hstAmountVal,
            expense_date: formData.expense_date,
            expense_type: 'business',
            description: formData.description || null,
          },
          performedBy: authUser?.id,
        });
        resetForm();
        loadExpenses();
      }
    }
    setIsSaving(false);
  };

  const handleDelete = async (expense: BusinessExpense) => {
    const { error } = await supabase
      .from("business_expenses")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", expense.id);
    if (error) {
      toast.error("Failed to delete");
    } else {
      toast.success("Expense deleted");
      await logAudit({
        entityType: 'expense',
        entityId: expense.id,
        action: 'deleted',
        metadata: {
          category: expense.category,
          amount: Number(expense.amount),
          expense_date: expense.expense_date,
          expense_type: 'business',
          description: expense.description || null,
        },
        performedBy: user?.id,
      });
      loadExpenses();
    }
    setDeleteTarget(null);
  };

  const handleExport = async () => {
    try {
      const data = filteredExpenses.map((e) => ({
        Date: new Date(e.expense_date + "T00:00:00").toLocaleDateString("en-CA"),
        Category: e.category,
        Description: e.description || "",
        "Amount (excl. HST)": Number(e.amount).toFixed(2),
        "HST Paid": Number(e.hst_amount).toFixed(2),
        "Total": (Number(e.amount) + Number(e.hst_amount)).toFixed(2),
        "Receipt": e.receipt_filename || "",
      }));

      await exportToExcel(
        [{ name: "Business Expenses", data }],
        `BusinessExpenses_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.success("Excel exported");
    } catch (error) {
      console.error("Failed to export business expenses:", error);
      toast.error("Failed to export expenses. Please try again.");
    }
  };

  // Apply both category and date filters
  const filteredExpenses = expenses.filter((e) => {
    const matchesCategory = filterCategory === "all" || e.category === filterCategory;
    const matchesDate = isInDateRange(e.expense_date);
    return matchesCategory && matchesDate;
  });

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    if (sortField === "date") {
      aVal = new Date(a.expense_date).getTime();
      bVal = new Date(b.expense_date).getTime();
    } else if (sortField === "category") {
      aVal = a.category.toLowerCase();
      bVal = b.category.toLowerCase();
    } else { // amount
      aVal = Number(a.amount) || 0;
      bVal = Number(b.amount) || 0;
    }

    if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const totalAmount = filteredExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const totalHst = filteredExpenses.reduce((sum, e) => sum + Number(e.hst_amount), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Business Expenses</h1>
          <p className="text-sm text-muted-foreground">Track general overhead costs</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={handleExport} disabled={expenses.length === 0} className="flex-1 sm:flex-none">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          {!isAdding && (
            <Button onClick={() => { resetForm(); setIsAdding(true); }} className="flex-1 sm:flex-none">
              <Plus className="h-4 w-4 mr-2" /> Add Expense
            </Button>
          )}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="p-4 flex sm:block items-center justify-between">
          <p className="text-xs text-muted-foreground">Total Expenses</p>
          <p className="text-lg sm:text-xl font-bold">${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex sm:block items-center justify-between">
          <p className="text-xs text-muted-foreground">HST Paid (ITCs)</p>
          <p className="text-lg sm:text-xl font-bold">${totalHst.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex sm:block items-center justify-between">
          <p className="text-xs text-muted-foreground">Grand Total</p>
          <p className="text-lg sm:text-xl font-bold">${(totalAmount + totalHst).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
        </CardContent></Card>
      </div>

      {/* Filters: Category + Date Period */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex gap-3 items-center">
          <Label className="shrink-0">Category:</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {BUSINESS_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>{cat.value}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-3 items-center">
          <Label className="shrink-0">Period:</Label>
          <Select value={datePeriod} onValueChange={setDatePeriod}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="last_quarter">Last Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Custom Date Range Picker */}
      {datePeriod === "custom" && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1">Start Date</Label>
                <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground mb-1">End Date</Label>
                <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Form */}
      {isAdding && (
        <form onSubmit={handleSubmit} className="border rounded-lg p-4 space-y-3 bg-muted/30">
          <h3 className="font-heading font-semibold text-sm">{editingExpense ? "Edit Expense" : "Add New Expense"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={handleCategoryChange}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {BUSINESS_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={formData.expense_date} onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })} required />
            </div>
            <div>
              <Label>Amount (before HST) *</Label>
              <Input type="number" step="0.01" min="0" placeholder="0.00" value={formData.amount} onChange={(e) => handleAmountChange(e.target.value)} required />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>HST Amount</Label>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">HST applies</Label>
                  <Switch checked={formData.hst_applicable} onCheckedChange={handleHstToggle} />
                </div>
              </div>
              <Input type="number" step="0.01" min="0" placeholder="0.00" value={formData.hst_amount}
                onChange={(e) => setFormData({ ...formData, hst_amount: e.target.value })} disabled={!formData.hst_applicable} />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional notes" rows={2} />
          </div>
          <div>
            <Label>Receipt</Label>
            <ReceiptUpload
              folderPath="business"
              category={formData.category || "Expense"}
              onUploaded={(url, filename) => setFormData({ ...formData, receipt_url: url, receipt_filename: filename })}
              existingUrl={formData.receipt_url}
              existingFilename={formData.receipt_filename}
              onRemoved={() => setFormData({ ...formData, receipt_url: null, receipt_filename: null })}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            <Button type="submit" disabled={isSaving || !formData.category || !formData.amount}>
              {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {editingExpense ? "Update Expense" : "Save Expense"}
            </Button>
          </div>
        </form>
      )}

      {/* Expense List */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading expenses...</div>
      ) : filteredExpenses.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          No business expenses recorded yet
        </CardContent></Card>
      ) : (
        <div>
          {/* Mobile: Card layout */}
          <div className="sm:hidden space-y-3">
            {sortedExpenses.map((expense) => (
              <div key={expense.id} className="border rounded-lg p-3 bg-card space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{expense.category}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(expense.expense_date + "T00:00:00").toLocaleDateString("en-CA")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">${Number(expense.amount).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">+${Number(expense.hst_amount).toFixed(2)} HST</p>
                  </div>
                </div>
                {expense.description && (
                  <p className="text-xs text-muted-foreground">{expense.description}</p>
                )}
                <div className="flex items-center justify-between pt-1 border-t">
                  <div className="flex-1 min-w-0">
                    {expense.receipt_url ? (
                      <ReceiptUpload
                        folderPath="business" category={expense.category}
                        onUploaded={() => {}}
                        existingUrl={expense.receipt_url}
                        existingFilename={expense.receipt_filename}
                      />
                    ) : <span className="text-xs text-muted-foreground">No receipt</span>}
                  </div>
                  <div className="flex gap-1 shrink-0 ml-2">
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground hover:text-foreground"
                      onClick={() => openEdit(expense)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(expense)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table layout */}
          <div className="hidden sm:block border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted select-none">
                  <tr>
                    <th onClick={() => toggleSort("date")} className="text-left p-3 font-medium cursor-pointer hover:bg-muted/80">
                      <span className="flex items-center gap-1">Date <ArrowUpDown className="h-3.5 w-3.5" /></span>
                    </th>
                    <th onClick={() => toggleSort("category")} className="text-left p-3 font-medium cursor-pointer hover:bg-muted/80">
                      <span className="flex items-center gap-1">Category <ArrowUpDown className="h-3.5 w-3.5" /></span>
                    </th>
                    <th className="text-left p-3 font-medium">Description</th>
                    <th onClick={() => toggleSort("amount")} className="text-right p-3 font-medium cursor-pointer hover:bg-muted/80">
                      <span className="flex items-center justify-end gap-1">Amount <ArrowUpDown className="h-3.5 w-3.5" /></span>
                    </th>
                    <th className="text-right p-3 font-medium">HST</th>
                    <th className="text-center p-3 font-medium">Receipt</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedExpenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-muted/50">
                      <td className="p-3 whitespace-nowrap">{new Date(expense.expense_date + "T00:00:00").toLocaleDateString("en-CA")}</td>
                      <td className="p-3">{expense.category}</td>
                      <td className="p-3 text-muted-foreground truncate max-w-[200px]">{expense.description || "—"}</td>
                      <td className="p-3 text-right">${Number(expense.amount).toFixed(2)}</td>
                      <td className="p-3 text-right text-muted-foreground">${Number(expense.hst_amount).toFixed(2)}</td>
                      <td className="p-3 text-center">
                        {expense.receipt_url ? (
                          <ReceiptUpload
                            folderPath="business" category={expense.category}
                            onUploaded={() => {}}
                            existingUrl={expense.receipt_url}
                            existingFilename={expense.receipt_filename}
                          />
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={() => openEdit(expense)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(expense)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="Delete Expense"
        description={deleteTarget ? `Delete "${deleteTarget.category}" expense of $${deleteTarget.amount}? This action cannot be undone.` : ""}
        confirmLabel="Delete"
        variant="destructive"
      />
    </div>
  );
}
